import { appContext } from '../../../../AppContext';
import { IbssPage } from '../../../../Components/Core/BasePage/IbssPage';
import { ApiConstants } from '../../../../Providers.Api/ApiConstants';
import { Tenancy } from './DataModels';
import Helper from '../../../../Common/Helper';
import { PagedResponse } from "@iconics/core";
import { JSX } from 'react';
import { IRouterProps, withRouting } from "@iconics/components";
import { Button, ButtonBar, Card, DataGrid, GridContainer, GridItem, IGridData, IGridRowsRequest, IGridRowsResponse, LayoutContainer, LayoutContent, RightButtons, TickSymbol } from "@iconics/components";
import { SerendieSymbol } from '@serendie/symbols';
import { IRouteParams } from '../Edit/EditTenancy';
import { ILanguageValues, LanguageValuesHelper } from "@iconics/localisation";

class ListTenancy extends IbssPage<IProps, IState>
{
    private languageValuesHelper: LanguageValuesHelper;
    private get local() { return appContext().localStorageProvider; }

    constructor(props: IProps)
    {
        super(props);
        this.languageValuesHelper = new LanguageValuesHelper(appContext().localisation);

        this.state =
        {
            canCreateTenancy: this.local.hasRight('DATAMODEL.Tenancy.Create'),
            gridData: { rows: [] },
        };
    }

    public async componentDidMount(): Promise<void>
    {
        this.pageTitle = appContext().labels.Platform_Tenancy_PageTitle;
    }

    private async handleRowsRequest(request: IGridRowsRequest<ITenancy>): Promise<IGridRowsResponse<ITenancy>>
    {
        const tenanciesData = await appContext().fadxPlatformApiClientV2.v2.core.tenancies.get<PagedResponse<Tenancy[]>>({
            select: Tenancy,
            skipToken: request.skipToken,
            top: request.take,
            orderBy: DataGrid.toODataOrderBy(request.sorter),
        });
        return { rows: tenanciesData.value.map(x => ({
                id: x.TenancyId,
                name: x.Name,
                displayName: this.languageValuesHelper.fromString(x.DisplayName),
                isEnabled: x.IsEnabled === 'Yes',
                eTag: x['@odata.etag'],
            } as ITenancy)), nextSkipToken: tenanciesData.skipToken };
    }

    public render(): JSX.Element
    {
        const labels = appContext().labels;
        return (
            <>
                <LayoutContainer>
                    <LayoutContent>
                        <Card
                            title={labels.Platform_Tenancy_ListTitle}
                            subTitle={labels.Platform_Tenancy_ListSubTitle}
                            icon={<SerendieSymbol name='layout-left' />}
                        >
                            <GridContainer>
                                <GridItem>
                                    <ButtonBar>
                                        <RightButtons>
                                            <Button
                                                size='small'
                                                styleType='ghost'
                                                leftIcon={<SerendieSymbol name='plus' />}
                                                onClick={() => this.props.navigate(`/admin/tenancies/new`)}
                                                disabled={!this.state.canCreateTenancy}
                                            >
                                                {labels.Platform_CommonAction_Add}
                                            </Button>
                                        </RightButtons>
                                    </ButtonBar>
                                </GridItem>
                                <GridItem>
                                    <DataGrid
                                        columns={[
                                            {
                                                columnId: Helper.nameOf<Tenancy>("Name"),
                                                title: labels.Tenancy_Name,
                                                getCellValue: (i) => i.name,
                                                sortable: true,
                                                columnWidth: 45,
                                            },
                                            {
                                                columnId: Helper.nameOf<Tenancy>("DisplayName"),
                                                title: labels.Tenancy_DisplayName,
                                                getCellValue: (i) => this.languageValuesHelper.getValue(i.displayName),
                                                sortable: true,
                                                columnWidth: 45,
                                            },
                                            {
                                                columnId: Helper.nameOf<Tenancy>("IsEnabled"),
                                                title: labels.Tenancy_IsEnabled,
                                                getCellValue: i => i.isEnabled,
                                                template: i => <TickSymbol value={i.value as boolean} />,
                                                sortable: true,
                                                columnWidth: 10,
                                            },
                                        ]}
                                        data={this.state.gridData}
                                        rowId={i => i.id}
                                        onChange={i => this.setState({ gridData: i })}
                                        onRowsRequest={i => this.handleRowsRequest(i)}
                                        onCellClicked={i => this.props.navigate(`/admin/tenancies/${i.id}/edit`)}
                                    />
                                </GridItem>
                            </GridContainer>
                        </Card>
                    </LayoutContent>
                </LayoutContainer>
            </>
        );
    }
}

export default withRouting(ListTenancy);

export interface IProps extends IRouterProps<IRouteParams>
{
}

export interface IState
{
    canCreateTenancy: boolean;
    gridData: IGridData<ITenancy>;
}

export interface ITenancy
{
    id: string;
    name: string;
    displayName: ILanguageValues,
    isEnabled: boolean;
    eTag: string;
}